import React, { useEffect } from "react";
import "../styles/CreateListing.scss";
import { categories, types, facilities } from "../assets/data/data";
import { IoRemoveCircleOutline } from "react-icons/io5";
import { AiOutlinePlusCircle } from "react-icons/ai";
import variables from "../styles/variables.scss";
import "../styles/confirmationToast.scss"; // to check error
import { IoIosImages } from "react-icons/io";
import { useState } from "react";
import { BiTrash } from "react-icons/bi";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

const UpdateListing = () => {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const { id } = useParams();
  const navigate = useNavigate();

  // State variables for form fields
  const [category, setCategory] = useState("");
  const [type, setType] = useState("");
  const [formLocation, setFormLocation] = useState({
    street: "",
    aptSuite: "",
    city: "",
    province: "",
    country: "",
  });
  const [guestCount, setGuestCount] = useState(null);
  const [bedroomCount, setBedroomCount] = useState(null);
  const [bedCount, setBedCount] = useState(null);
  const [bathroomCount, setBathroomCount] = useState(null);
  const [amenities, setAmenities] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [photoUrls, setPhotoUrls] = useState([]); // URLs of existing photos
  const [newPhotos, setNewPhotos] = useState([]); // Newly selected files
  const [isLoading, setIsLoading] = useState(false);

  const [formDescription, setFormDescription] = useState({
    title: "",
    description: "",
    pricePerNight: null,
  });

  useEffect(() => {
    // Fetch listing data
    const fetchListingData = async () => {
      try {
        const token = localStorage.getItem("admin");
        const response = await fetch(`${API_BASE_URL}/listing/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          // Update form fields with fetched data
          setCategory(data.category);
          setType(data.type);
          setFormLocation({
            street: data.street,
            aptSuite: data.aptSuite,
            city: data.city,
            province: data.province,
            country: data.country,
          });
          setGuestCount(data.guestCount);
          setBedroomCount(data.bedroomCount);
          setBedCount(data.bedCount);
          setBathroomCount(data.bathroomCount);
          setFormDescription({
            title: data.title,
            description: data.description,
            pricePerNight:
              data.pricePerNight &&
              parseFloat(data.pricePerNight.$numberDecimal),
          });
          // Set amenities
          const amenitiesArray = data.amenities[0].split(",");
          setAmenities(amenitiesArray);
          // Set photos
          // setPhotos(data.signedUrls);
          setPhotoUrls(data.signedUrls);
        } else {
          console.error("Failed to fetch listing data:", response.statusText);
        }
      } catch (error) {
        console.error("Failed to fetch listing data:", error.message);
      }
    };

    fetchListingData();
  }, [id]);

  // Function to handle location change
  const handleChangeLocation = (e) => {
    const { name, value } = e.target;
    setFormLocation((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // Function to handle amenity selection
  const handleSelectAmenities = (amenityName) => {
    setAmenities((prevAmenities) => {
      if (prevAmenities.includes(amenityName)) {
        return prevAmenities.filter((amenity) => amenity !== amenityName);
      } else {
        return [...prevAmenities, amenityName];
      }
    });
  };

  // Function to handle photo upload
  // const handleUploadPhotos = (e) => {
  //   const files = Array.from(e.target.files);
  //   setPhotos((prevPhotos) => [...prevPhotos, ...files]);
  // };

  // Function to handle photo removal
  // const handleRemovePhoto = (indexToRemove) => {
  //   setPhotos((prevPhotos) =>
  //     prevPhotos.filter((_, index) => index !== indexToRemove)
  //   );
  // };

  // Function to handle description change
  const handleChangeDescription = (e) => {
    const { name, value } = e.target;
    setFormDescription((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // Function to handle form submission
  const handleUpdate = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    try {
      // Prepare the updated listing data
      const formData = new FormData();

      // Append form fields
      formData.append("category", category);
      formData.append("type", type);
      formData.append("location[street]", formLocation.street);
      formData.append("location[aptSuite]", formLocation.aptSuite);
      formData.append("location[city]", formLocation.city);
      formData.append("location[province]", formLocation.province);
      formData.append("location[country]", formLocation.country);
      formData.append("guestCount", guestCount);
      formData.append("bedroomCount", bedroomCount);
      formData.append("bedCount", bedCount);
      formData.append("bathroomCount", bathroomCount);
      formData.append("amenities", amenities.join(","));
      formData.append("description[title]", formDescription.title);
      formData.append("description[description]", formDescription.description);
      formData.append(
        "description[pricePerNight]",
        formDescription.pricePerNight
      );
      // Append new photos
      newPhotos.forEach((photo) => {
        formData.append("listingPhotos", photo.file);
      });
      photoUrls.forEach((url, index) => {
        formData.append(`oldPhotos[${index}]`, url);
      });

      const token = localStorage.getItem("admin");

      // Send a PUT request to update the listing
      const response = await fetch(`${API_BASE_URL}/listing/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      console.log("Response:", response);

      if (response.ok) {
        console.log("Listing updated successfully");
        toast.success("Listing updated successfully");
        navigate("/manage-listings");
        setIsLoading(false);
      } else {
        console.error("Failed to update listing:", response.statusText);
        toast.error("Failed to update listing");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Failed to update listing:", error.message);
      toast.error("Failed to update listing");
      setIsLoading(false);
    }
  };

  const handleUploadPhotos = (event) => {
    const files = Array.from(event.target.files);
    const newFiles = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setNewPhotos([...newPhotos, ...newFiles]);
  };

  const handleRemovePhoto = (index, isNewPhoto) => {
    if (isNewPhoto) {
      setNewPhotos(newPhotos.filter((_, i) => i !== index));
    } else {
      setPhotoUrls(photoUrls.filter((_, i) => i !== index));
    }
  };

  return (
    <>
      <div className="create-listing">
        <h1>Update Your Place</h1>
        <form onSubmit={handleUpdate}>
          <div className="create-listing_step1">
            <h2>Step 1: Tell us about your place</h2>
            <hr />
            <h3>Which of these categories best describes your place?</h3>
            <div className="category-list">
              {categories?.map((item, index) => (
                <div
                  className={`category ${
                    category === item.label ? "selected" : ""
                  }`}
                  key={index}
                  onClick={() => setCategory(item.label)}
                >
                  <div className="category_icon">{item.icon}</div>
                  <p>{item.label}</p>
                </div>
              ))}
            </div>

            <h3>What type of place will guests have?</h3>
            <div className="type-list">
              {types?.map((item, index) => (
                <div
                  className={`type ${type === item.name ? "selected" : ""}`}
                  key={index}
                  onClick={() => setType(item.name)}
                >
                  <div className="type_text">
                    <h4>{item.name}</h4>
                    <p>{item.description}</p>
                  </div>
                  <div className="type_icon">{item.icon}</div>
                </div>
              ))}
            </div>

            <h3>Where's your place located?</h3>
            <div className="full">
              <div className="location">
                <p>Street Address</p>
                <input
                  type="text"
                  placeholder="Street"
                  name="street"
                  value={formLocation.street}
                  onChange={handleChangeLocation}
                  required
                />
              </div>
            </div>

            <div className="half">
              <div className="location">
                <p>Apartment, Suite, etc. (if applicable)</p>
                <input
                  type="text"
                  placeholder="Apt, Suite, etc. (if applicable)"
                  name="aptSuite"
                  value={formLocation.aptSuite}
                  onChange={handleChangeLocation}
                />
              </div>
              <div className="location">
                <p>City</p>
                <input
                  type="text"
                  placeholder="City"
                  name="city"
                  value={formLocation.city}
                  onChange={handleChangeLocation}
                  required
                />
              </div>
            </div>

            <div className="half">
              <div className="location">
                <p>Province</p>
                <input
                  type="text"
                  placeholder="Province"
                  name="province"
                  value={formLocation.province}
                  onChange={handleChangeLocation}
                  required
                />
              </div>
              <div className="location">
                <p>Country</p>
                <input
                  type="text"
                  placeholder="Country"
                  name="country"
                  value={formLocation.country}
                  onChange={handleChangeLocation}
                  required
                />
              </div>
            </div>

            <h3>Share some basics about your place</h3>
            <div className="basics">
              <div className="basic">
                <p>Guests</p>
                <div className="basic_count">
                  <IoRemoveCircleOutline
                    onClick={() => {
                      guestCount > 1 && setGuestCount(guestCount - 1);
                    }}
                    sx={{
                      fontSize: "25px",
                      cursor: "pointer",
                      "&:hover": { color: variables.pinkred },
                    }}
                  />
                  <p>{guestCount}</p>
                  <AiOutlinePlusCircle
                    onClick={() => {
                      setGuestCount(guestCount + 1);
                    }}
                    sx={{
                      fontSize: "25px",
                      cursor: "pointer",
                      "&:hover": { color: variables.pinkred },
                    }}
                  />
                </div>
              </div>

              <div className="basic">
                <p>Bedrooms</p>
                <div className="basic_count">
                  <IoRemoveCircleOutline
                    onClick={() => {
                      bedroomCount > 1 && setBedroomCount(bedroomCount - 1);
                    }}
                    sx={{
                      fontSize: "25px",
                      cursor: "pointer",
                      "&:hover": { color: variables.pinkred },
                    }}
                  />
                  <p>{bedroomCount}</p>
                  <AiOutlinePlusCircle
                    onClick={() => {
                      setBedroomCount(bedroomCount + 1);
                    }}
                    sx={{
                      fontSize: "25px",
                      cursor: "pointer",
                      "&:hover": { color: variables.pinkred },
                    }}
                  />
                </div>
              </div>

              <div className="basic">
                <p>Beds</p>
                <div className="basic_count">
                  <IoRemoveCircleOutline
                    onClick={() => {
                      bedCount > 1 && setBedCount(bedCount - 1);
                    }}
                    sx={{
                      fontSize: "25px",
                      cursor: "pointer",
                      "&:hover": { color: variables.pinkred },
                    }}
                  />
                  <p>{bedCount}</p>
                  <AiOutlinePlusCircle
                    onClick={() => {
                      setBedCount(bedCount + 1);
                    }}
                    sx={{
                      fontSize: "25px",
                      cursor: "pointer",
                      "&:hover": { color: variables.pinkred },
                    }}
                  />
                </div>
              </div>

              <div className="basic">
                <p>Bathrooms</p>
                <div className="basic_count">
                  <IoRemoveCircleOutline
                    onClick={() => {
                      bathroomCount > 1 && setBathroomCount(bathroomCount - 1);
                    }}
                    sx={{
                      fontSize: "25px",
                      cursor: "pointer",
                      "&:hover": { color: variables.pinkred },
                    }}
                  />
                  <p>{bathroomCount}</p>
                  <AiOutlinePlusCircle
                    onClick={() => {
                      setBathroomCount(bathroomCount + 1);
                    }}
                    sx={{
                      fontSize: "25px",
                      cursor: "pointer",
                      "&:hover": { color: variables.pinkred },
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="create-listing_step2">
            <h2>Step 2: Make your place stand out</h2>
            <hr />

            <h3>Tell guests what your place has to offer</h3>
            <div className="amenities">
              {facilities?.map((item, index) => (
                <div
                  className={`facility ${
                    amenities.includes(item.name) ? "selected" : ""
                  }`}
                  key={index}
                  onClick={() => handleSelectAmenities(item.name)}
                >
                  <div className="facility_icon">{item.icon}</div>
                  <p>{item.name}</p>
                </div>
              ))}
            </div>

            <h3>Add some photos of your place</h3>
            <div className="photos">
              {photoUrls.length < 1 && newPhotos.length < 1 && (
                <>
                  <input
                    id="image"
                    type="file"
                    style={{ display: "none" }}
                    accept="image/*"
                    onChange={handleUploadPhotos}
                    multiple
                  />
                  <label htmlFor="image" className="alone">
                    <div className="icon">
                      <IoIosImages />
                    </div>
                    <p>Upload from your device</p>
                  </label>
                </>
              )}

              {(photoUrls.length >= 1 || newPhotos.length >= 1) && (
                <>
                  {photoUrls.map((photoUrl, index) => (
                    <div key={`url-${index}`} className="photo">
                      <img src={photoUrl} alt={`Uploaded place ${index + 1}`} />
                      <button
                        type="button"
                        onClick={() => handleRemovePhoto(index, false)}
                      >
                        <BiTrash />
                      </button>
                    </div>
                  ))}
                  {newPhotos.map((photo, index) => (
                    <div key={`new-${index}`} className="photo">
                      <img
                        src={photo.preview}
                        alt={`Newly uploaded place ${index + 1}`}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemovePhoto(index, true)}
                      >
                        <BiTrash />
                      </button>
                    </div>
                  ))}
                  <input
                    id="image"
                    type="file"
                    style={{ display: "none" }}
                    accept="image/*"
                    onChange={handleUploadPhotos}
                    multiple
                  />
                  <label htmlFor="image" className="together">
                    <div className="icon">
                      <IoIosImages />
                    </div>
                    <p>Upload more photos</p>
                  </label>
                </>
              )}
            </div>

            <h3>What make your place attractive and exciting?</h3>
            <div className="description">
              <p>Title</p>
              <input
                type="text"
                placeholder="Title"
                name="title"
                value={formDescription.title}
                onChange={handleChangeDescription}
                required
              />
              <p>Description</p>
              <textarea
                type="text"
                placeholder="Description"
                name="description"
                value={formDescription.description}
                onChange={handleChangeDescription}
                required
              />
              <p>Now, set your PRICE</p>
              <span>$</span>
              <input
                type="number"
                name="pricePerNight"
                value={formDescription.pricePerNight}
                onChange={handleChangeDescription}
                className="price"
                required
              />
            </div>

            <button
              className={`${isLoading ? "loading_submit_btn" : "submit_btn"} `}
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? "Updating...." : "Update Listing"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default UpdateListing;
